import React, { useState } from 'react';
import { Box, Typography, Chip, Paper, Tooltip, IconButton } from '@mui/material';
import { Edit, Add, Delete } from '@mui/icons-material';
import { DataTable } from '@shared/components/DataTable/DataTable';
import { PageHeader } from '@shared/components/PageHeader';
import { LoadingSpinner, ConfirmationDialog } from '@shared/components';
import { useRoles } from '../hooks/useRoles';
import { EditRoleDialog } from '../components/EditRoleDialog';
import type { Role, CreateRoleRequest, UpdateRoleRequest } from '../types';
import { format } from 'date-fns';
import StatusChip from '@shared/components/StatusChip/StatusChip';

const RolesList: React.FC = () => {
  const { roles, isLoading, createRole, updateRole, deleteRole, isCreating, isUpdating, isDeleting } = useRoles();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  const handleCreateClick = () => {
    setSelectedRole(null);
    setDialogMode('create');
    setEditDialogOpen(true);
  };

  const handleEditClick = (role: Role) => {
    setSelectedRole(role);
    setDialogMode('edit');
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (role: Role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setSelectedRole(null);
  };

  const handleSaveRole = async (data: CreateRoleRequest | UpdateRoleRequest) => {
    if (dialogMode === 'create') {
      await createRole(data as CreateRoleRequest);
    } else if (selectedRole) {
      await updateRole({ roleId: selectedRole.role_id, data: data as UpdateRoleRequest });
    }
  };

  const handleConfirmDelete = async () => {
    if (!roleToDelete) return;
    await deleteRole(roleToDelete.role_id);
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const columns = [
    {
      id: 'name',
      label: 'Role Name',
      minWidth: 180,
      format: (value: string) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {value}
        </Typography>
      ),
    },
    {
      id: 'is_active',
      label: 'Status',
      minWidth: 100,
      format: (value: boolean) => (
        <StatusChip label={value ? 'Active' : 'Inactive'} status={value ? 'success' : 'default'} />
      ),
    },
    {
      id: 'module',
      label: 'Permissions',
      minWidth: 300,
      format: (value: any[]) => {
        const totalPermissions = value.reduce((sum, m) => sum + m.permissions.length, 0);
        const moduleCount = value.length;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <StatusChip label={`${moduleCount} modules`} size="small" status="primary" variant="outlined" />

            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  {value.map((mod) => (
                    <Box key={mod.module_name} sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                        {mod.module_name}:
                      </Typography>
                      <Typography variant="caption" sx={{ ml: 0.5 }}>
                        {mod.permissions.join(', ')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              }
              placement="top"
            >
              <StatusChip label={`${totalPermissions} permissions`} size="small" status="success" />
            </Tooltip>
          </Box>
        );
      },
    },
    {
      id: 'created_at',
      label: 'Created At',
      minWidth: 160,
      format: (value: string) => <Typography variant="body2">{format(new Date(value), 'MMM dd, yyyy')}</Typography>,
    },
    {
      id: 'updated_at',
      label: 'Updated At',
      minWidth: 160,
      format: (value: string) => <Typography variant="body2">{format(new Date(value), 'MMM dd, yyyy')}</Typography>,
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 120,
      align: 'center' as const,
      sortable: false,
      format: (value: any, row: Role) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Tooltip title="Edit Role">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEditClick(row)}
              sx={{ '&:hover': { backgroundColor: 'primary.lighter' } }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Role">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(row)}
              sx={{ '&:hover': { backgroundColor: 'error.lighter' } }}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (isLoading) {
    return <LoadingSpinner message="Loading roles..." fullHeight />;
  }

  return (
    <Box sx={{ px: 1, pt: 1 }}>
      <PageHeader
        title="Role Management"
        subtitle="Manage roles and permissions"
        primaryActionLabel="Create Role"
        primaryIcon={<Add />}
        onPrimaryAction={handleCreateClick}
      />

      <Paper variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
        <DataTable
          columns={columns}
          data={roles}
          loading={isLoading}
          page={0}
          rowsPerPage={roles.length}
          totalRows={roles.length}
          onPageChange={() => {}}
          onRowsPerPageChange={() => {}}
          onSort={() => {}}
          orderBy=""
          order="asc"
          emptyMessage="No roles found"
          columnVisibilityKey="roles_table"
        />
      </Paper>

      <EditRoleDialog
        open={editDialogOpen}
        role={selectedRole}
        onClose={handleCloseDialog}
        onSave={handleSaveRole}
        isSubmitting={isCreating || isUpdating}
        mode={dialogMode}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!isDeleting) setDeleteDialogOpen(false);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${roleToDelete?.name}"? This action cannot be undone.`}
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        variant="error"
        confirmColor="error"
      />
    </Box>
  );
};

export default RolesList;
